import { useMemo } from "react";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import { isViewersStoredOption, type OptionViewer, type OwnOptionT } from "./ownOptions";

/**
 * Own options known in this browser: ones just added or renamed (shown before the page reloads its rows), and,
 * on an anonymous public link, ones that only exist here until the form is submitted. `entry_id` says which
 * entry an option was added in, so a new entry starts clean.
 */
export interface StoredOwnOptionT extends OwnOptionT {
    entry_id: string;
    /** Who added it, or null on an anonymous public link. Every account signed in on this browser shares the store. */
    user_id: string | null;
    /** True until saved to the database: anonymous links save their own options on submit. */
    pending: boolean;
}

type State = {
    own_options: StoredOwnOptionT[];
};

type Actions = {
    /** Adds the option, or replaces the one with the same id (a rename, or a pending one now saved). */
    upsertOwnOption: (option: StoredOwnOptionT) => void;
    /** Forgets an option that was never saved (deleted before an anonymous submit). */
    removeOwnOption: (optionId: string) => void;
    /** The options added in this entry. */
    getForEntry: (entryId: string) => StoredOwnOptionT[];
    /** Drops options added in any other entry. */
    keepOnlyEntry: (entryId: string) => void;
    /** Forgets every option, when the account signs out. */
    clear: () => void;
};

export const useOwnOptionsStore = create<State & Actions>()(
    persist(
        (set, get) => ({
            own_options: [],
            upsertOwnOption: (option) =>
                set((state) => ({
                    own_options: state.own_options.some((existing) => existing.option_id === option.option_id)
                        ? state.own_options.map((existing) =>
                              existing.option_id === option.option_id ? option : existing
                          )
                        : [...state.own_options, option],
                })),
            removeOwnOption: (optionId) =>
                set((state) => ({
                    own_options: state.own_options.filter((option) => option.option_id !== optionId),
                })),
            getForEntry: (entryId) => get().own_options.filter((option) => option.entry_id === entryId),
            keepOnlyEntry: (entryId) =>
                set((state) => ({ own_options: state.own_options.filter((option) => option.entry_id === entryId) })),
            clear: () => set({ own_options: [] }),
        }),
        {
            name: "own-options-store",
            storage: createJSONStorage(() => localStorage),
            // Version 0 rows have no user_id, so whose they are is unknown: drop them.
            version: 1,
            migrate: () => ({ own_options: [] }),
        }
    )
);

/** The stored own options that belong to this viewer (see isViewersStoredOption). */
export function useViewersOwnOptions(viewer: OptionViewer): StoredOwnOptionT[] {
    const stored = useOwnOptionsStore((state) => state.own_options);
    const { userId, entryId } = viewer;
    return useMemo(
        () => stored.filter((option) => isViewersStoredOption(option, { userId, entryId })),
        [stored, userId, entryId]
    );
}
